import React from 'react';
import { DESKTOP_RENDERER_ID, MOBILE_RENDERER_ID } from 'crazyEditor/core/constants/editorConstants';
import * as publishUtils from './publishUtils';
import _ from 'lodash';
import DesktopRenderer from 'crazyEditor/components/EditorStage/DesktopView/DesktopRenderer';
import MobileRenderer from 'crazyEditor/components/EditorStage/MobileView/MobileRenderer';
import ReactDOMServer from 'react-dom/server';
import { getIsMobileOptimized, getTemplateCSSUrl } from 'crazyEditor/redux/modules/template/templateSelectors';
import { getNormalizedStaticDesktopElements, getNormalizedDesktopElementsForRender, getStaticMobileElements, getVisibleMobileElementsForRender } from 'crazyEditor/redux/modules/element/elementSelectors';
import { getStaticAssets, getFontList } from 'crazyEditor/redux/modules/editor/editorSelectors';
import { store } from 'crazyEditor/redux/store';
import cheerio from 'cheerio';

const DEFAULT_WEB_FONTS = ['Open Sans'];

function lowerCaseAndRemoveQuotes(str) {
  return _.trim(str, '"').toLowerCase().replace(/'/g, '');
}

function getFontsFromHtml(html) {
  const element = document.createElement('div');
  element.innerHTML = html;
  const allInnerElements = element.getElementsByTagName('*');
  const fontsUsed = {};
  _.forEach(allInnerElements, element => {
    let {fontFamily} = element.style;
    if (!_.isEmpty(fontFamily) && _.isString(fontFamily)) {
      fontFamily = fontFamily.toLowerCase().replace(/['"]/g, '');
      fontsUsed[fontFamily] = true;
    }
  });
  return _.keys(fontsUsed);
}

function getFontsByIntersection(usedFonts, editorFontList) {
  const intersection = [];
  _.forEach(editorFontList, (editorFont) => {
    if (usedFonts.indexOf(lowerCaseAndRemoveQuotes(editorFont)) !== -1) {
      intersection.push(editorFont);
    }
  });
  return intersection;
}

function getFonts(usedFonts, editorFontList) {
  const getFontIntersection = getFontsByIntersection(usedFonts, editorFontList);
  const stylesheets = [
    `https://fonts.googleapis.com/css?family=${_.uniq(DEFAULT_WEB_FONTS.concat(getFontIntersection)).join('|')}`,
    `https://netdna.bootstrapcdn.com/font-awesome/4.1.0/css/font-awesome.min.css`
  ];
  return [...publishUtils.addStylesheets(stylesheets)].join('');
}

function getStyles(staticAssets, shouldRenderMobileView, simpleTextEnforcement) {
  const stylesheets = [
    staticAssets.styles
  ];
  return [...publishUtils.addStylesheets(stylesheets, shouldRenderMobileView, simpleTextEnforcement)].join('');
}

function getStaticMarkup(desktopProps, mobileProps, shouldRenderMobileView) {
  const renderProps = {
    autoHeightTextElement: store.getState().user.autoHeightTextElement,
    publish: true,
  };
  return ReactDOMServer.renderToStaticMarkup(<div>
    <DesktopRenderer {...desktopProps} {...renderProps}/>
    {shouldRenderMobileView && [
      <style key="styles" dangerouslySetInnerHTML={{__html: publishUtils.getResponsiveLayoutStyles(DESKTOP_RENDERER_ID, MOBILE_RENDERER_ID, mobileProps)}}/>,
      publishUtils.addMobileDescription(store.getState().listingData.description),
      <MobileRenderer key="mobile-renderer" {...mobileProps} {...renderProps}/>
    ]}
    <a id="footer-link" href="https://cgi6.ebay.com/ws/eBayISAPI.dll?SolutionsDirectory&page=details&solutionId=24825" style={{display: 'none'}} data-exclude-css-removal/>
  </div>);
}

function getUsedFonts(desktopElements) {
  let usedFontList = [];
  _.forEach(desktopElements, (element) => {
    // TODO: Move this to elementMethods - and use like - elementMethods.getFontFamily()
    if (element.compProps.fontFamily) {
      usedFontList.push(lowerCaseAndRemoveQuotes(element.compProps.fontFamily));
    }
    if (_.get(element, 'data.compProps.fontFamily')) {
      usedFontList.push(lowerCaseAndRemoveQuotes(element.data.compProps.fontFamily));
    }
    if (element.data.rawHTML) {
      usedFontList = usedFontList.concat(getFontsFromHtml(element.data.rawHTML));
    }
  });
  return _.uniq(usedFontList);
}

function fixLinks(publishCode) {
  const $ = cheerio.load(publishCode);
  $('a:not([target])').attr('target', '_blank');
  return $.html();
}

export default {
  getPublishCode: (removeStyles = true, minifyDisabled = undefined) => {
    const state = store.getState();
    const shouldRenderMobileView = getIsMobileOptimized(state);
    const desktopElements = getNormalizedDesktopElementsForRender(state);
    const staticDesktopElements = getNormalizedStaticDesktopElements(state);
    const mobileElements = getVisibleMobileElementsForRender(state);
    const staticMobileElements = getStaticMobileElements(state);
    const {description} = state.listingData;
    const simpleTextEnforcement = !_.isEmpty(description);
    const {template: {id, s3VersionId}} = state;
    let draftItemId;
    if (state.routing.location) {
      const itemId = state.routing.location.query.itemId;
      draftItemId = itemId && itemId.indexOf('DRAFT') === 0 ? itemId : null;
    }
    if (_.isNil(minifyDisabled)) {
      minifyDisabled = state.templateTagSet.minifyDisabled;

    }
    const editorFontList = getFontList(state);

    const desktopProps = {
      desktopElements,
      staticDesktopElements,
      showAds: false,
      viewStyles: {},
      wrapperStyles: {}
    };
    const mobileProps = {
      mobileElements,
      staticMobileElements,
      desktopElements,
      staticDesktopElements,
      showAds: false,
      viewStyles: {}
    };
    const publishCodeArray = [].concat(
      '<head>',
      publishUtils.getMetaTags(shouldRenderMobileView, {
        'cl-draft-item-id': draftItemId,
        'cl-template-id': id,
        'cl-template-s3-version-id': s3VersionId,
      }),
      getStyles(getStaticAssets(state).publish, shouldRenderMobileView, simpleTextEnforcement),
      getFonts(getUsedFonts(desktopElements), editorFontList),
      '</head>',
      getStaticMarkup(desktopProps, mobileProps, shouldRenderMobileView),
    );
    if (simpleTextEnforcement) {
      publishCodeArray.push(`<div id="cl-raw-description" data-exclude-css-removal=true style="display: none;">${description}</div>`);
    }
    let publishCode = publishCodeArray.join('');
    publishCode = publishCode.replace(/[\x00-\x09\x0B\x0C\x0E-\x1F\x7F]/g, '');
    publishCode = publishCode.replace(/<style[^>]*>[\s\S]*?<\/style>/g, (str) => {return str.replace(/\s+/g, ' ');});
    if (!minifyDisabled) {
      try {
        publishCode = require('html-minifier')(publishCode);
      } catch (e) {
        console.warn('Minification failed, using original code');
      }
    }
    publishCode = fixLinks(publishCode);
    if (removeStyles) {
      return publishUtils.removeStyles(publishCode, getTemplateCSSUrl(state));
    }
    return {publishCode: publishCode, templateCSS: null};
  }
};
